/**
 * Addon Product Block
 *
 * Adds the checked addon AFTER the main product is added, in sequence,
 * and hands the theme a cart response that already includes the addon.
 * This avoids the two common causes of "sometimes it doesn't add":
 *   1. Two cart requests racing each other (one overwrites the other).
 *   2. The cart drawer rendering before the addon request finished.
 *
 * If the addon turns out to be sold out (422), the block switches to the
 * sold-out state and the main product is still added normally.
 */
(() => {
  if (window.__addonProductBlockInit) return;
  window.__addonProductBlockInit = true;

  const CART_ADD_URL = `${window.Shopify?.routes?.root || '/'}cart/add.js`;
  const PENDING_TTL_MS = 15000;

  /* ---------- Custom element ---------- */

  class AddonProductBlock extends HTMLElement {
    get checkbox() {
      return this.querySelector('.addon-product-block__checkbox');
    }

    get variantId() {
      return Number(this.dataset.variantId) || 0;
    }

    get isAvailable() {
      return this.dataset.available === 'true';
    }

    get isSelected() {
      return this.isAvailable && this.variantId > 0 && !!this.checkbox?.checked;
    }

    markSoldOut() {
      this.dataset.available = 'false';
      this.classList.add('addon-product-block--sold-out');
      const cb = this.checkbox;
      if (cb) {
        cb.checked = false;
        cb.disabled = true;
        cb.setAttribute('aria-disabled', 'true');
      }
      this.querySelector('[data-sold-out-tag]')?.removeAttribute('hidden');
    }
  }

  if (!customElements.get('addon-product-block')) {
    customElements.define('addon-product-block', AddonProductBlock);
  }

  /* ---------- Remember which addons belong to the form being submitted ---------- */

  let pending = null;

  const isCartAddForm = (form) =>
    form instanceof HTMLFormElement && /\/cart\/add/.test(form.getAttribute('action') || '');

  // Only look inside the same section / modal so a quick-add on another
  // product never picks up the main product's addon.
  const findSelectedAddons = (form) => {
    const scope = form.closest('dialog, [role="dialog"], .shopify-section') || document;
    const blocks = [...scope.querySelectorAll('addon-product-block')].filter((b) => b.isSelected);
    const seen = new Set();
    return blocks.filter((b) => (seen.has(b.variantId) ? false : seen.add(b.variantId)));
  };

  const capture = (form) => {
    if (!isCartAddForm(form)) return;
    const blocks = findSelectedAddons(form);
    pending = blocks.length ? { blocks, expires: Date.now() + PENDING_TTL_MS } : null;
  };

  document.addEventListener('submit', (e) => capture(e.target), true);
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest?.('button[type="submit"], button[name="add"], input[type="submit"]');
      if (btn?.form) capture(btn.form);
    },
    true
  );

  const takePending = () => {
    const job = pending;
    pending = null;
    return job && job.expires > Date.now() ? job : null;
  };

  /* ---------- Helpers ---------- */

  const isCartAddRequest = (input, init) => {
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (method !== 'POST') return false;
    const raw = typeof input === 'string' ? input : input?.url || String(input);
    try {
      return /\/cart\/add(\.js)?$/.test(new URL(raw, location.origin).pathname);
    } catch {
      return false;
    }
  };

  const readSections = (body) => {
    try {
      if (body instanceof FormData || body instanceof URLSearchParams) return body.get('sections');
      if (typeof body === 'string') return JSON.parse(body).sections || null;
    } catch {
      /* ignore */
    }
    return null;
  };

  const originalFetch = window.fetch.bind(window);

  const addAddon = async (block, sections) => {
    const payload = { items: [{ id: block.variantId, quantity: 1 }] };
    if (sections) {
      payload.sections = sections;
      payload.sections_url = location.pathname;
    }

    try {
      const res = await originalFetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // 422 = sold out / not enough stock
        if (res.status === 422) block.markSoldOut();
        console.warn('[addon-product] Addon not added:', data.description || data.message || res.status);
        return null;
      }
      return data;
    } catch (err) {
      console.warn('[addon-product] Addon request failed:', err);
      return null;
    }
  };

  /* ---------- Wrap fetch: main add first, then addon, then return ---------- */

  window.fetch = async function (input, init) {
    if (!isCartAddRequest(input, init)) return originalFetch(input, init);

    const job = takePending();
    if (!job) return originalFetch(input, init);

    const response = await originalFetch(input, init);
    if (!response.ok) return response; // main product failed, don't add addon

    let data;
    try {
      data = await response.clone().json();
    } catch {
      return response;
    }

    const sections = readSections(init?.body);
    let latestSections = null;

    // One request per addon, in order, so a sold-out addon can't block another
    for (const block of job.blocks) {
      if (!block.isSelected) continue;
      const result = await addAddon(block, sections);
      if (result?.sections) latestSections = result.sections;
    }

    // Give the theme the section HTML from AFTER the addon was added,
    // so the cart drawer / bubble show both items.
    if (latestSections) {
      data.sections = { ...(data.sections || {}), ...latestSections };
    }

    return new Response(JSON.stringify(data), {
      status: response.status,
      statusText: response.statusText,
      headers: { 'Content-Type': 'application/json' },
    });
  };
})();